import { useContext, useEffect } from "react";
import { GrowthBookContext } from "@growthbook/growthbook-react";
import Auth from "../Auth";
import {
  IMPORT_SPEED_PERCEPTION_COHORT_KEY,
  IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY,
  isImportSpeedPerceptionTreatmentCohort,
} from "../../../src/shared/constants";
import { useSudowriteStore } from "../stores/sudowriteStore";
import useUserMetadata from "./useUserMetadata";

let lastLoggedFlagKey = null;

function logImportSpeedPerceptionFlag(growthbookOn, effective, isTrial, persistedCohort) {
  const source = persistedCohort ? "persisted cohort" : "growthbook";
  const logKey = `${source}:${growthbookOn}:${effective}:${isTrial}:${persistedCohort ?? "none"}`;
  if (lastLoggedFlagKey === logKey) return;
  lastLoggedFlagKey = logKey;
  console.log(
    `[GrowthBook] ${IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY} = ${effective}`,
    { source, growthbookRaw: growthbookOn, isTrial, persistedCohort }
  );
}

/**
 * SW-9080 treatment for new imports. GrowthBook assigns while on trial; cohort is
 * persisted on the user and survives subscribe.
 */
export default function useImportSpeedPerceptionExperiment() {
  const { growthbook } = useContext(GrowthBookContext) ?? {};
  const subscription = useSudowriteStore((state) => state.subscription);
  const { userMetadata, refetch: refetchUserMetadata } = useUserMetadata();
  const isTrial = !!(subscription?.fetched && subscription.activeTrial());
  const persistedCohort = userMetadata?.experiments?.[IMPORT_SPEED_PERCEPTION_COHORT_KEY] ?? null;
  const persistedTreatment = isImportSpeedPerceptionTreatmentCohort(persistedCohort);
  const growthbookOn = isTrial ? (growthbook?.isOn?.(IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY) ?? false) : false;
  const effective = persistedCohort ? persistedTreatment : isTrial && growthbookOn;

  useEffect(() => {
    if (!isTrial || !subscription?.fetched || persistedCohort || !growthbook) {
      return;
    }

    const treatment = growthbook.isOn(IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY);
    Auth.fetchWithIdToken("/api/user-metadata/import-speed-cohort", { treatment })
      .then(() => refetchUserMetadata())
      .catch((err) => console.warn("[GrowthBook] failed to persist import-speed cohort", err));
  }, [isTrial, subscription?.fetched, persistedCohort, growthbook, refetchUserMetadata]);

  useEffect(() => {
    logImportSpeedPerceptionFlag(growthbookOn, effective, isTrial, persistedCohort);
  }, [growthbookOn, effective, isTrial, persistedCohort]);

  return effective;
}

/** True when this user/session should use the SW-9080 flow. */
export function isImportSpeedPerceptionActive(experimentOn, projectId) {
  return !!(experimentOn || isImportSpeedPerceptionSession(projectId));
}

/** Persist trial cohort before import so server-side checks see it immediately. */
export async function persistImportSpeedPerceptionCohortIfNeeded(treatment, persistedCohort) {
  if (persistedCohort != null) return false;
  try {
    await Auth.fetchWithIdToken("/api/user-metadata/import-speed-cohort", { treatment });
    return true;
  } catch (err) {
    console.warn("[GrowthBook] failed to persist import-speed cohort", err);
    return false;
  }
}

export function markImportSpeedPerceptionSession(projectId) {
  if (projectId) {
    sessionStorage.setItem(`importSpeedPerception:${projectId}`, "1");
  }
}

export function isImportSpeedPerceptionSession(projectId) {
  return !!projectId && sessionStorage.getItem(`importSpeedPerception:${projectId}`) === "1";
}
